import React from 'react';
import PrintLayout from '../PrintLayout';

const DEFINITION = "The first component of a great meeting is a simple process. Most teams don't have a bad process, but simply no process at all. Much of their structure is based on old habits, assumptions, and unwritten rules. These components can help leaders and teams determine the process that works for them, and Patrick Lencioni has developed several simple frameworks that teams can utilize.";

const ELEMENTS = [
  {
    n: 1, title: 'Clear Roles',
    body: 'Although this may seem obvious, many meetings are functionally leaderless because no one clearly takes responsibility for making sure the meeting produces the right outcomes. The meeting leader is ultimately accountable for the effectiveness of the meeting. A secondary role may also be assigned to a facilitator, who can focus primarily on meeting process, efficiency, participation, and productive or unproductive behaviors.',
  },
  {
    n: 2, title: 'Helpful Rules',
    body: 'Healthy teams establish meeting "norms" that they have discussed and agreed upon. Meeting norms may be similar across organizations, but each team has its own distinct style and expectations. Norms can address both team behaviors and meeting mechanics. Click here to access a library of potential norms.',
  },
  {
    n: 3, title: 'Functional Parts',
    body: 'Meetings do not need to be complicated or overly procedural. They do, however, need more structure than simply moving through a list of topics. The structure should provide context about both the team and the organization, ensure the most important topics are discussed, and give unresolved issues a clear place to go. Every meeting should reserve enough time at the end to clarify decisions, actions, and messages.',
  },
  {
    n: 4, title: 'Effective Tools',
    body: 'Meeting tools vary by organization. Examples include presentation slides, scoreboards, shared documents, technology and devices, real-time chat and polls, pre-reads, agendas, action-tracking systems, recordings, and AI tools. Teams should be clear about which tools they use and whether those tools improve the meeting or create distractions. Tools and meeting rules should support one another.',
  },
  {
    n: 5, title: 'Decisions and Actions',
    body: 'Decision-making should be an explicit part of a meeting process. Teams should be clear about who makes the final decision, how decisions are recorded, how actions are assigned, and when each action is due. There is no single correct decision-making model. The important issue is that teams understand and use a clear approach.',
  },
  {
    n: 6, title: 'Cascading Messages',
    body: 'People outside the meeting often wonder what was discussed and what it meant for them. A meeting should identify the key messages that need to be communicated outside the room, what must remain confidential, who needs to hear each message, and who is responsible for communicating it.',
  },
];

export default function PrintProcessMethodology({ pageNumber }) {
  return (
    <PrintLayout pageNumber={pageNumber}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, display: 'flex', background: '#f5f4f0' }}>
        <div style={{ width: '34%', background: '#1e2e45', padding: '6% 5%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '2.5px', color: '#5f7942', textTransform: 'uppercase', marginBottom: 14, fontFamily: 'Montserrat, sans-serif' }}>
            Section 4: Simple Process
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 27, fontWeight: 700, color: '#fff', lineHeight: 1.15, marginBottom: 18 }}>
            Meeting stew is a problem.
          </div>
          <div style={{ width: 36, height: 2, background: '#5f7942', marginBottom: 16, borderRadius: 1 }} />
          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, marginBottom: 16 }}>
            {DEFINITION}
          </div>
          <div style={{ padding: '10px 12px', borderLeft: '2px solid #5f7942', fontSize: 10, fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
            "The single biggest structural problem facing leaders of meetings is the tendency to throw every type of issue that needs to be discussed into the same meeting, like a bad stew with too many random ingredients."
            <div style={{ marginTop: 6, fontStyle: 'normal', fontWeight: 600, color: 'rgba(255,255,255,0.3)' }}>
              — Patrick Lencioni, <i>Death by Meeting</i>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: '4.5% 4%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 19, fontWeight: 700, color: '#1e2e45', marginBottom: 12 }}>
            How to Avoid Meeting Stew?
          </div>
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(3, 1fr)', gap: 10 }}>
            {ELEMENTS.map(({ n, title, body }) => (
              <div key={n} style={{
                background: 'white', borderRadius: 10, border: '1px solid rgba(42,61,91,0.1)',
                borderLeft: '4px solid #2a3d5b', padding: '10px 12px',
                display: 'flex', gap: 10, alignItems: 'flex-start', overflow: 'hidden',
              }}>
                <div style={{
                  width: 23, height: 23, borderRadius: 6, background: 'rgba(42,61,91,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  fontWeight: 800, color: '#2a3d5b', fontSize: 10.5,
                }}>
                  {n}
                </div>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: '#2a3d5b', marginBottom: 3, letterSpacing: '0.02em' }}>{title}</div>
                  <div style={{ fontSize: 9, color: '#4a5568', lineHeight: 1.45 }}>{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PrintLayout>
  );
}
